import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { withRetry, supabaseQuery, getErrorMessage } from '@/lib/apiUtils';

export const useFavorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch user's favorites
  const fetchFavorites = async () => {
    if (!user) return;
    
    try {
      const { data } = await supabaseQuery<{ opportunity_id: string }[]>(
        supabase
          .from('opportunity_favorites')
          .select('opportunity_id')
          .eq('user_id', user.id)
      );
      
      setFavorites(data?.map(fav => fav.opportunity_id) || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast({
        title: "Erreur de chargement",
        description: getErrorMessage(error),
        variant: "destructive"
      });
    }
  };

  // Check if opportunity is favorited
  const isFavorited = (opportunityId: string) => {
    return favorites.includes(opportunityId);
  };

  // Toggle favorite status
  const toggleFavorite = async (opportunityId: string) => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour ajouter des favoris",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const isCurrentlyFavorited = isFavorited(opportunityId);
      
      if (isCurrentlyFavorited) {
        // Remove from favorites
        const { error } = await supabase
          .from('opportunity_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('opportunity_id', opportunityId);
        
        if (error) throw error;
        
        setFavorites(prev => prev.filter(id => id !== opportunityId));
        
        toast({
          title: "Retiré des favoris",
          description: "L'opportunité a été retirée de vos favoris"
        });
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('opportunity_favorites')
          .insert({
            user_id: user.id,
            opportunity_id: opportunityId
          });
        
        if (error) throw error;
        
        setFavorites(prev => [...prev, opportunityId]);
        
        toast({
          title: "Ajouté aux favoris",
          description: "L'opportunité a été ajoutée à vos favoris"
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier les favoris",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Get user's favorite opportunities with full details
  const getFavoriteOpportunities = async () => {
    if (!user) return [];
    
    try {
      // First, get the favorite opportunity IDs with retry logic
      const { data: favoriteIds } = await supabaseQuery<{ opportunity_id: string; created_at: string }[]>(
        supabase
          .from('opportunity_favorites')
          .select('opportunity_id, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
      );
      
      if (!favoriteIds || favoriteIds.length === 0) {
        return [];
      }
      
      // Then get the opportunity details with retry logic
      const opportunityIds = favoriteIds.map(fav => fav.opportunity_id);
      const { data: opportunities } = await supabaseQuery<any[]>(
        supabase
          .from('opportunities')
          .select('*')
          .in('id', opportunityIds)
      );
      
      // Sort opportunities by the order they were favorited
      const sortedOpportunities = favoriteIds.map(fav => 
        opportunities?.find(opp => opp.id === fav.opportunity_id)
      ).filter(Boolean);
      
      return sortedOpportunities;
    } catch (error) {
      console.error('Error fetching favorite opportunities:', error);
      toast({
        title: "Erreur de chargement",
        description: getErrorMessage(error),
        variant: "destructive"
      });
      return [];
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [user]);

  return {
    favorites,
    isFavorited,
    toggleFavorite,
    getFavoriteOpportunities,
    loading,
    refetch: fetchFavorites
  };
};